import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { pnlColor } from "../chart/chartColors";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
  color,
  isDark,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  isDark: boolean;
}) {
  return (
    <div className="rounded-lg px-3 py-2 bg-muted/40 flex flex-col justify-center gap-0.5 min-w-0 min-h-0 h-full overflow-hidden">
      <p className={`text-[9px] font-semibold tracking-widest uppercase truncate widget-label`}>
        {label}
      </p>
      <p className="text-lg font-bold font-mono tabular-nums leading-tight truncate" style={{ color: color ?? "inherit" }}>
        {value}
      </p>
      {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
    </div>
  );
}

export default function AnalyticsKpisWidget() {
  const { analyticsData, metrics, isDark, t } = useDashboardData();

  if (!analyticsData || !metrics) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.analyticsKpis} isDark={isDark} compact />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const { calmarRatio, recoveryFactor } = analyticsData;

  // token-based good/bad color
  const good = pnlColor(1);
  const bad = pnlColor(-1);

  const sharpeValue =
    metrics.sharpeRatio !== null ? metrics.sharpeRatio.toFixed(2) : "—";
  const sharpeColor =
    metrics.sharpeRatio !== null && metrics.sharpeRatio > 0 ? good : bad;

  const drawdownValue = `${metrics.maxDrawdown.toFixed(2)} $`;
  const drawdownColor = metrics.maxDrawdown < 0 ? bad : "inherit";

  const expectancyValue = `${metrics.expectancy >= 0 ? "+" : ""}${metrics.expectancy.toFixed(2)} $`;
  const expectancyColor = metrics.expectancy >= 0 ? good : bad;

  const calmarValue =
    calmarRatio !== null ? calmarRatio.toFixed(2) : "—";
  const calmarColor =
    calmarRatio !== null && calmarRatio > 0 ? good : bad;

  const recoveryValue =
    recoveryFactor !== null
      ? isFinite(recoveryFactor)
        ? recoveryFactor.toFixed(2)
        : "∞"
      : "—";
  const recoveryColor =
    recoveryFactor !== null && recoveryFactor > 0 ? good : bad;

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={t.dashboard.widgets.analyticsKpis} isDark={isDark} compact />
      <CardContent className="flex-1 p-3 min-h-0 overflow-hidden">
        <div className="grid grid-cols-3 gap-2 h-full" style={{ gridTemplateRows: "1fr 1fr" }}>
          <KpiTile
            label={t.analytics.sharpeRatio}
            value={sharpeValue}
            sub={t.analytics.riskAdjustedReturn}
            color={sharpeColor}
            isDark={isDark}
          />
          <KpiTile
            label={t.analytics.maxDrawdown}
            value={drawdownValue}
            sub={t.analytics.peakToTrough}
            color={drawdownColor}
            isDark={isDark}
          />
          <KpiTile
            label={t.analytics.expectancy}
            value={expectancyValue}
            sub={t.analytics.averagePerTrade}
            color={expectancyColor}
            isDark={isDark}
          />
          <KpiTile
            label={t.analytics.calmarRatio}
            value={calmarValue}
            sub={t.analytics.calmarRatioDesc}
            color={calmarColor}
            isDark={isDark}
          />
          <KpiTile
            label={t.analytics.recoveryFactor}
            value={recoveryValue}
            sub={t.analytics.recoveryFactorDesc}
            color={recoveryColor}
            isDark={isDark}
          />
          <KpiTile
            label={t.analytics.winRate}
            value={`${metrics.winRate.toFixed(1)}%`}
            sub={`${metrics.winningTrades}W · ${metrics.losingTrades}L`}
            color={metrics.winRate >= 50 ? good : bad}
            isDark={isDark}
          />
        </div>
      </CardContent>
    </Card>
  );
}
